/**
 * tradeFlags — pure per-trade signal extraction from `trade.barAnalysis`.
 *
 * Where the coach analyzers operate at AGGREGATE scopes (day, setup, ATM,
 * progress) and surface patterns across many trades, this module surfaces
 * what a SINGLE trade's bar analysis says about that trade. The output is
 * a small array of chip-shaped objects suitable for inline rendering on a
 * trade card or in a tooltip.
 *
 * Why a separate module instead of another analyzer?
 *   - Analyzers consume an array of trades and look for patterns. A trade
 *     card doesn't care about patterns — it cares about THIS trade.
 *   - These flags are pure projections of barAnalysis fields. No
 *     thresholds depending on the rest of the dataset, no ranking, no
 *     observation envelope. Just direct signal → label.
 *   - Coach observations are heavyweight (id, severity, evidence,
 *     confidence, priority, action text). Flags are 4 fields.
 *
 * Reads:
 *   trade.barAnalysis.{maeR, mfeR, emasAtEntry, vwapAtEntry, avwapAtEntry, keyLevels}
 *   trade.netPnl, trade.entry, trade.direction
 *
 * Returns: Array<Flag> where Flag = { key, label, severity, hint }
 *   - key       stable identifier for React keys + dedupe
 *   - label     short visible text on the chip (e.g. "Sharp entry")
 *   - severity  'good' | 'warn' | 'info' — drives chip color
 *   - hint      one-sentence tooltip explaining the flag
 *
 * Trades without barAnalysis return [] silently — backfill is the
 * prerequisite for any flags to fire.
 */

import { detectAverageDown } from './averageDownDetector.js';

// Thresholds — chosen to match the day/setup analyzer thresholds where
// a per-trade flag corresponds to a multi-trade pattern (so the chip
// fires for the same trades that contribute to the aggregate signal).
const SHARP_ENTRY_MAX_MAER       = 0.20;
const WICK_STOP_MIN_MFER         = 0.80;
const FULL_RISK_MIN_MAER         = 0.95;
const MFE_LEAK_MAX_CAPTURE       = 0.30;
const ROUND_TRIP_MIN_MFER        = 1.50;
const ROUND_TRIP_MAX_NET_R       = 0.40;
const BAGHELD_MIN_MFER           = 0.50;

// Panic exit (Wave A, v7.4.4): a loser exited very fast that DIDN'T
// reach the stop. Means the trader bailed out of fear, not because the
// trade invalidated. Hougaard's "Best Loser Wins" calls this the most
// common discipline leak. Per-trade flag; the day rollup analyzer
// aggregates the count.
const PANIC_EXIT_MAX_MINUTES     = 2;     // exit within 2 min of entry
const PANIC_EXIT_MAX_MAER        = 0.50;  // MAE never reached half the stop

// Parse entry/exit "HH:MM" strings to minutes-of-day, returning the
// elapsed minutes between them (exit - entry). Returns null when either
// side can't be parsed. Designed for SAME-DAY trades since `entryTime` /
// `exitTime` carry no date.
const elapsedMinutes = (entryTime, exitTime) => {
  if (!entryTime || !exitTime || typeof entryTime !== 'string' || typeof exitTime !== 'string') return null;
  const parse = (s) => {
    const m = s.match(/^(\d{1,2}):(\d{2})/);
    if (!m) return null;
    const hh = parseInt(m[1], 10);
    const mm = parseInt(m[2], 10);
    if (!Number.isFinite(hh) || !Number.isFinite(mm)) return null;
    return hh * 60 + mm;
  };
  const e = parse(entryTime);
  const x = parse(exitTime);
  if (e === null || x === null) return null;
  const diff = x - e;
  // Negative diff would suggest the times are nonsense for same-day; bail.
  return diff >= 0 ? diff : null;
};

const flag = (key, label, severity, hint) => ({ key, label, severity, hint });

// ── Stack helpers (mirror dayBarContext / setupBarContext) ────────────────

const classifyStack = (emasAtEntry, direction) => {
  if (!emasAtEntry || typeof emasAtEntry !== 'object') return 'neutral';
  const periods = Object.keys(emasAtEntry)
    .map(k => parseInt(k, 10))
    .filter(n => Number.isFinite(n) && n >= 1)
    .sort((a, b) => a - b);
  if (periods.length < 2) return 'neutral';
  const values = periods.map(p => emasAtEntry[p] ?? emasAtEntry[String(p)]);
  if (!values.every(v => Number.isFinite(v))) return 'neutral';
  let bullish = true, bearish = true;
  for (let i = 0; i < values.length - 1; i++) {
    if (values[i] <= values[i + 1]) bullish = false;
    if (values[i] >= values[i + 1]) bearish = false;
  }
  if (!bullish && !bearish) return 'neutral';
  const isLong = direction !== 'short';
  if (bullish) return isLong ? 'with' : 'against';
  return isLong ? 'against' : 'with';
};

// ── Public API ────────────────────────────────────────────────────────────

/**
 * Compute the flag array for a single trade.
 *
 * @param {Object} trade
 * @returns {Array<{key:string,label:string,severity:string,hint:string}>}
 */
export const computeTradeFlags = (trade) => {
  if (!trade || typeof trade !== 'object') return [];

  const flags = [];

  // ── Non-bar flags (run BEFORE the barAnalysis bail) ────────────
  // Some flags don't depend on bar-walk analysis — they only need the
  // trade's fill data. Compute these first so trades without
  // barAnalysis (recently synced, pre-backfill) still get the chips
  // that don't need bar context.
  //
  // Currently: averaged-down (only needs trade.entries + direction).
  const avgDown = detectAverageDown(trade);
  if (avgDown && avgDown.averagedDown) {
    const adds = avgDown.addCount === 1 ? '1 add' : `${avgDown.addCount} adds`;
    flags.push(flag(
      'averaged-down',
      'Averaged down',
      'warn',
      `Added ${adds} to a losing position (${avgDown.totalAddedQty} contracts, worst add ${avgDown.worstAdverse}pts underwater). Adding to losers is the cardinal Hougaard violation — even when it works, it teaches the wrong reflex.`
    ));
  }

  // ── Bar-dependent flags (bail if no barAnalysis) ───────────────
  const a = trade.barAnalysis;
  if (!a || typeof a !== 'object') return flags;

  const { maeR, mfeR, mfeDollars } = a;
  const netPnl = Number.isFinite(trade.netPnl) ? trade.netPnl : null;
  const isWinner = netPnl != null && netPnl > 0;
  const isLoser  = netPnl != null && netPnl < 0;

  // Sharp entry: tiny MAE before the trade worked. Requires winner — the
  // semantic ("clean entry that worked") is undermined if the trade was a
  // stop-out. Without this gate, short-duration stop-outs whose bar data
  // didn't fully capture the adverse move would falsely fire the chip.
  if (
    isWinner
    && Number.isFinite(maeR) && maeR <= SHARP_ENTRY_MAX_MAER
    && Number.isFinite(mfeR) && mfeR > 0.5
  ) {
    flags.push(flag(
      'sharp-entry',
      'Sharp entry',
      'good',
      `Only ${(maeR * 100).toFixed(0)}% of risk against you before the trade worked. Clean entry timing.`
    ));
  }

  // Full risk taken — lost trades that pushed all the way to stop.
  // Useful as a counterpoint to sharp-entry: the entry was at a level
  // the market reached without giving room.
  if (isLoser && Number.isFinite(maeR) && maeR >= FULL_RISK_MIN_MAER) {
    flags.push(flag(
      'full-risk',
      'Full 1R',
      'warn',
      'Trade went the full distance to stop with little hesitation. Stop placement or entry timing.'
    ));
  }

  // Wicked out — losers that came close to working.
  if (isLoser && Number.isFinite(mfeR) && mfeR >= WICK_STOP_MIN_MFER) {
    flags.push(flag(
      'wicked-out',
      'Wicked out',
      'warn',
      `Loser, but reached ${(mfeR * 100).toFixed(0)}% of target before reversing. Stop may be too tight.`
    ));
  }

  // Bag-held — losers that crossed half the way to target before stopping.
  // Different from wicked-out (that's near-full); this is the "should have
  // taken something" mid-zone.
  if (isLoser && Number.isFinite(mfeR) && mfeR >= BAGHELD_MIN_MFER && mfeR < WICK_STOP_MIN_MFER) {
    flags.push(flag(
      'bag-held',
      'Bag-held',
      'warn',
      `Crossed ${(mfeR * 100).toFixed(0)}% to target before turning into a loser. A scratch was available.`
    ));
  }

  // Entry discipline — Stalker / Chaser (Wave B.2, v7.4.4).
  // The discipline tag is computed by entryDiscipline.js and persisted on
  // barAnalysis.entryDiscipline. Surfaces here as a chip for instant
  // visual feedback on the trade card. Stalker is the cross-cutting
  // "stalking beats chasing" principle from the educator research.
  if (a.entryDiscipline && typeof a.entryDiscipline === 'object') {
    const { discipline, reason } = a.entryDiscipline;
    if (discipline === 'stalker') {
      flags.push(flag(
        'stalker-entry',
        'Stalker',
        'good',
        reason || 'Waited for the pullback before entering — high-quality entry mechanics.'
      ));
    } else if (discipline === 'chaser') {
      flags.push(flag(
        'chaser-entry',
        'Chaser',
        'warn',
        reason || 'Entered at a fresh extreme without retracement — classic FOMO-shape entry.'
      ));
    }
    // 'neutral' produces no chip — silent middle ground keeps the UI clean.
  }

  // (averaged-down chip computed above, before the barAnalysis bail —
  // it only needs trade.entries, not bar data.)

  // ── Setup pattern chips (Wave C, v7.4.5) ─────────────────────────
  // Each detected pattern surfaces as its own chip. Wolf Stack +
  // 3-Bar Play + Holy Grail + Uno Reverse + Signal Bar + H2/L2 are
  // all "good" (positive signal). Turtle Soup is contextual — if the
  // trade was on the reversal side (alignsWithTrade=true) it's good;
  // otherwise it's a warning (entered into a failed-breakout reversal).
  if (Array.isArray(a.setupPatterns) && a.setupPatterns.length > 0) {
    for (const p of a.setupPatterns) {
      if (p.type === 'wolf-stack') {
        if (p.score >= 3) {
          flags.push(flag(
            'wolf-stack',
            `Wolf Stack ${p.score}/${p.maxScore}`,
            'good',
            `Diaz Wolf Stack alignment: ${p.score}/${p.maxScore}. ${p.details}`
          ));
        } else if (p.score <= 1) {
          flags.push(flag(
            'wolf-stack-weak',
            `Wolf Stack ${p.score}/${p.maxScore}`,
            'warn',
            `Only ${p.score}/${p.maxScore} of the Wolf Stack agreed with entry. ${p.details}`
          ));
        }
      } else if (p.type === 'signal-bar-quality') {
        if (p.score === 3) {
          flags.push(flag(
            'signal-bar-strong',
            'Strong signal bar',
            'good',
            `Brooks signal bar: 3/3 — ${p.details}`
          ));
        } else if (p.score === 0) {
          flags.push(flag(
            'signal-bar-weak',
            'Weak signal bar',
            'warn',
            `Signal bar quality: 0/3 — ${p.details}`
          ));
        }
      } else if (p.type === 'holy-grail') {
        flags.push(flag(
          'holy-grail',
          'Holy Grail',
          'good',
          `Raschke Holy Grail: ${p.details}`
        ));
      } else if (p.type === '3-bar-play') {
        flags.push(flag(
          'three-bar-play',
          '3-Bar Play',
          'good',
          `Rake 3-Bar Play: ${p.details}`
        ));
      } else if (p.type === 'turtle-soup') {
        if (p.alignsWithTrade) {
          flags.push(flag(
            'turtle-soup-aligned',
            'Turtle Soup',
            'good',
            `Raschke Turtle Soup — ${p.details}`
          ));
        } else {
          flags.push(flag(
            'turtle-soup-against',
            'Turtle Soup ⚠',
            'warn',
            `Bought INTO a failed-breakout reversal. ${p.details}`
          ));
        }
      } else if (p.type === 'uno-reverse') {
        flags.push(flag(
          'uno-reverse',
          'Uno Reverse',
          'good',
          `Rake Uno Reverse: ${p.details}`
        ));
      } else if (p.type === 'h2' || p.type === 'l2') {
        flags.push(flag(
          p.type === 'h2' ? 'brooks-h2' : 'brooks-l2',
          p.type.toUpperCase(),
          'good',
          `Brooks ${p.type.toUpperCase()}: ${p.details}`
        ));
      } else if (p.type === '9-21-cross') {
        // 9/21 EMA Cross (Top-Down Phase D, v7.6.0). Three chip variants:
        //   - Entered at Entry 1 (5 EMA pullback, Diaz's canonical setup) — 'good'
        //   - Entered at Entry 2 (cross-price retest) — 'good'
        //   - Cross aligned with trade direction but not at a marked entry — 'info'
        //   - Cross opposite trade direction — 'warn' (counter-trend)
        if (p.enteredAtEntry1) {
          flags.push(flag(
            'macross-921-entry1',
            `9/21 Cross · Entry 1`,
            'good',
            `Diaz first-pullback to 5 EMA after the ${p.direction.toUpperCase()} 9/21 cross. ${p.details}`
          ));
        } else if (p.enteredAtEntry2) {
          flags.push(flag(
            'macross-921-entry2',
            `9/21 Cross · Entry 2`,
            'good',
            `Retest of the ${p.direction.toUpperCase()} 9/21 cross price (~${p.crossPrice.toFixed(2)}). ${p.details}`
          ));
        } else if (p.alignsWithTrade) {
          flags.push(flag(
            'macross-921-aligned',
            `9/21 Cross ${p.direction.toUpperCase()}`,
            'info',
            `Recent ${p.direction.toUpperCase()} 9/21 cross ${p.barsSinceCross} bar(s) before entry — trade aligned but not at a marked Entry 1/2.`
          ));
        } else {
          flags.push(flag(
            'macross-921-counter',
            `9/21 Cross ⚠`,
            'warn',
            `Trade direction opposes a recent 9/21 cross (${p.direction.toUpperCase()}, ${p.barsSinceCross} bar(s) ago). Counter-trend entry — different rules apply.`
          ));
        }
      }
    }
  }

  // Panic exit (Wave A, v7.4.4): a small loser exited very fast where
  // the MAE never came close to the stop. Means the trader bailed out
  // of fear before the trade invalidated. Hougaard calls this the most
  // common cause of asymmetric loss-size ratios — losers should be
  // exited at invalidation, not at discomfort.
  //
  // Gates:
  //   - isLoser (only fires on losing trades)
  //   - MAE < 50% of stop distance (didn't get to stop)
  //   - Trade duration < 2 minutes (exited fast)
  //
  // Mutually exclusive with full-risk / wicked-out / bag-held because
  // those require larger MAE or MFE. So this chip only fires on the
  // "left the trade before it could work" pattern.
  if (
    isLoser
    && Number.isFinite(maeR) && maeR < PANIC_EXIT_MAX_MAER
    && trade.entryTime && trade.exitTime
  ) {
    const mins = elapsedMinutes(trade.entryTime, trade.exitTime);
    if (mins !== null && mins <= PANIC_EXIT_MAX_MINUTES) {
      flags.push(flag(
        'panic-exit',
        'Panic exit',
        'warn',
        `Exited in ${mins === 0 ? '<1' : mins}m with only ${(maeR * 100).toFixed(0)}% of risk against you — bailed before the trade invalidated. Stop should do the exiting, not fear.`
      ));
    }
  }

  // MFE leak — winner that capped well below available move.
  if (isWinner && Number.isFinite(mfeDollars) && mfeDollars > 0 && netPnl != null) {
    const cap = Math.max(0, Math.min(1, netPnl / mfeDollars));
    if (cap < MFE_LEAK_MAX_CAPTURE) {
      flags.push(flag(
        'mfe-leak',
        'MFE leak',
        'warn',
        `Captured ${(cap * 100).toFixed(0)}% of the move. Targets too tight or exited early.`
      ));
    }
  }

  // Round-trip — trade that reached big MFE but ended modestly.
  // Only fires when mfeR is large AND we have a net-R-ish proxy via mfeDollars.
  if (Number.isFinite(mfeR) && mfeR >= ROUND_TRIP_MIN_MFER && netPnl != null && Number.isFinite(mfeDollars) && mfeDollars > 0) {
    const cap = netPnl / mfeDollars;
    if (cap > 0 && cap <= ROUND_TRIP_MAX_NET_R) {
      flags.push(flag(
        'round-trip',
        'Round-tripped',
        'warn',
        `Reached ${mfeR.toFixed(1)}R favorable but kept only ${(cap * 100).toFixed(0)}%. Letting winners slip.`
      ));
    }
  }

  // Counter-trend — entry against EMA stack direction.
  const stance = classifyStack(a.emasAtEntry, trade.direction);
  if (stance === 'against') {
    flags.push(flag(
      'counter-trend',
      'Counter-trend',
      'info',
      'EMA stack opposed entry direction on entry timeframe. Counter-trend trade — different rules apply.'
    ));
  } else if (stance === 'with') {
    flags.push(flag(
      'with-stack',
      'With stack',
      'good',
      'EMA stack aligned with entry direction on entry timeframe. Trend-following context.'
    ));
  }

  // HTF alignment — captured at entry by maeMfe.computeMaeMfe when bars from
  // higher timeframes are fed in. Each TF gets its own stance classification;
  // the trader workflow ("4H/1H/15M structure → 5M entry") cares about whether
  // each higher frame agrees or fights the entry.
  //
  // Two layers of flags:
  //   - Per-TF flags (`against-1h`, `against-4h`) for surgical signal — these
  //     are the ones the day rollup narrates: "you fought the 1H trend 3 times"
  //   - Aggregate flags (`htf-aligned`, `htf-mixed`) for one-glance summary on
  //     the trade card — captures whether ALL HTFs agreed or there was conflict
  if (a.htfEmas && typeof a.htfEmas === 'object') {
    const htfStances = {};
    for (const [tf, emas] of Object.entries(a.htfEmas)) {
      const s = classifyStack(emas, trade.direction);
      if (s !== 'neutral') htfStances[tf] = s;
    }
    const stanceList = Object.entries(htfStances);
    if (stanceList.length > 0) {
      const withCount = stanceList.filter(([, s]) => s === 'with').length;
      const againstCount = stanceList.filter(([, s]) => s === 'against').length;

      // Aggregate flag: all HTFs agree → htf-aligned (good); any disagreement → htf-mixed (info)
      if (againstCount === 0 && withCount > 0) {
        flags.push(flag(
          'htf-aligned',
          'HTF aligned',
          'good',
          `${withCount === 1 ? 'The' : 'All'} higher timeframe${withCount === 1 ? '' : 's'} (${stanceList.map(([tf]) => tf).join(', ')}) agreed with entry direction. Trend-with-trend context.`
        ));
      } else if (againstCount > 0 && withCount > 0) {
        const againstTfs = stanceList.filter(([, s]) => s === 'against').map(([tf]) => tf).join(', ');
        const withTfs = stanceList.filter(([, s]) => s === 'with').map(([tf]) => tf).join(', ');
        flags.push(flag(
          'htf-mixed',
          'HTF mixed',
          'info',
          `Higher timeframes split — ${withTfs} agreed, ${againstTfs} opposed. Counter-rotation between frames.`
        ));
      }

      // Per-TF flags for the most-traded HTFs. Each one is its own warn
      // signal because "fighting the 1H trend" is a more actionable lesson
      // than a generic htf-aligned/mixed badge.
      for (const [tf, s] of stanceList) {
        if (s === 'against') {
          flags.push(flag(
            `against-${tf}`,
            `Against ${tf.toUpperCase()}`,
            'warn',
            `Entry opposed the ${tf.toUpperCase()} EMA stack. Fighting bigger structure carries lower expectancy unless this is a deliberate counter-rotation play.`
          ));
        }
      }
    }
  }

  // VWAP alignment — only fires when vwapAtEntry is captured.
  if (Number.isFinite(a.vwapAtEntry) && Number.isFinite(parseFloat(trade.entry))) {
    const entryPx = parseFloat(trade.entry);
    const isLong = trade.direction !== 'short';
    const aboveVwap = entryPx > a.vwapAtEntry;
    const aligned = (isLong && aboveVwap) || (!isLong && !aboveVwap);
    if (!aligned) {
      flags.push(flag(
        'vwap-against',
        'VWAP against',
        'warn',
        `Entry on the ${isLong ? 'short' : 'long'} side of session VWAP — institutional flow opposed.`
      ));
    }
  }

  // Key-level tag (Wave E.2, v7.5.1) — fires when entry price is within a
  // small tolerance of a STATIC horizontal reference level (Golden Crayon,
  // prior-day OHLC, today's open + intraday extremes). Distinct from the
  // AVWAP tags below: those are trailing volume-weighted averages, these
  // are flat lines traders draw on their charts.
  if (a.keyLevels && typeof a.keyLevels === 'object' && Number.isFinite(parseFloat(trade.entry))) {
    const entryPx = parseFloat(trade.entry);
    // Same proximity heuristic as the AVWAP tag below — 0.1% of price.
    const tol = Math.max(entryPx * 0.001, 0.5);
    // The user's primary inflection level (Golden Crayon) gets a 'good'
    // chip; others get 'info'. Golden Crayon's prominence here matches
    // the user-stated trading philosophy.
    const KEY_LEVEL_SPECS = [
      { field: 'goldenCrayon', label: 'Golden Crayon',      severity: 'good',
        hint: 'Entry at the prior-day midpoint — bull/bear inflection.' },
      { field: 'prevDayHigh',  label: 'Prior Day High',      severity: 'info',
        hint: 'Entry at yesterday’s high — classic resistance / breakout-retest level.' },
      { field: 'prevDayLow',   label: 'Prior Day Low',       severity: 'info',
        hint: 'Entry at yesterday’s low — classic support / breakdown-retest level.' },
      { field: 'prevDayClose', label: 'Prior Day Close',     severity: 'info',
        hint: 'Entry near yesterday’s close — gap-fill reference.' },
      { field: 'todayOpen',    label: 'Today’s Open',   severity: 'info',
        hint: 'Entry near today’s opening price — opening-drive reference.' },
    ];
    for (const spec of KEY_LEVEL_SPECS) {
      const v = a.keyLevels[spec.field];
      if (!Number.isFinite(v)) continue;
      const dist = Math.abs(entryPx - v);
      if (dist <= tol) {
        flags.push(flag(
          `key-level-${spec.field}`,
          `Tagged ${spec.label}`,
          spec.severity,
          `${spec.hint} (level: ${v.toFixed(2)}, within ${tol.toFixed(2)} pts)`
        ));
      }
    }
  }

  // AVWAP tag (Wave D, v7.5.1) — fires when entry price is within a small
  // tolerance of any anchored VWAP captured at entry. AVWAPs from prior-
  // day high/low/close + session high/low are common institutional levels;
  // a trade entered AT one of them is reacting to a known reference point.
  // Tolerance is in PRICE POINTS, scaled by the symbol's tick size when
  // available, otherwise a fixed fraction of price.
  if (a.avwapAtEntry && typeof a.avwapAtEntry === 'object' && Number.isFinite(parseFloat(trade.entry))) {
    const entryPx = parseFloat(trade.entry);
    // Use 0.1% of price as proximity tolerance — covers most futures
    // contracts (MNQ ~0.1% = ~3 pts at 3000, plenty for AVWAP tag
    // detection without false positives at distance).
    const tol = Math.max(entryPx * 0.001, 0.5);
    const ANCHOR_LABELS = {
      'prior-day-high':  'PDH',
      'prior-day-low':   'PDL',
      'prior-day-close': 'PDC',
      'session-high':    'HoD',
      'session-low':     'LoD',
    };
    for (const [anchorKey, value] of Object.entries(a.avwapAtEntry)) {
      if (!Number.isFinite(value)) continue;
      const dist = Math.abs(entryPx - value);
      if (dist <= tol) {
        const label = ANCHOR_LABELS[anchorKey] || anchorKey;
        flags.push(flag(
          `avwap-tag-${anchorKey}`,
          `Tagged AVWAP ${label}`,
          'info',
          `Entry within ${tol.toFixed(2)} pts of the AVWAP anchored at ${label} (${value.toFixed(2)}) — institutional reference level reaction.`
        ));
      }
    }
  }

  // Volume Profile tag (Top-Down B.4 + E, v7.6.0) — fires when entry price
  // is within tolerance of any volume-profile level (POC / VAH / VAL / HVNs
  // / LVNs). Same 0.1% tolerance as sibling level tags. POC is the highest-
  // volume price in the analyzed window — the most-accepted price by
  // institutional flow — so its chip gets 'good' severity. VAH/VAL bound
  // the 70% value area — institutional acceptance edges — 'info'. HVNs are
  // local volume peaks ('info'); LVNs are thin areas ('info' but the
  // tooltip flags them as fast-traversal zones, useful context for entries).
  if (a.volumeProfile && typeof a.volumeProfile === 'object' && Number.isFinite(parseFloat(trade.entry))) {
    const entryPx = parseFloat(trade.entry);
    const tol = Math.max(entryPx * 0.001, 0.5);
    const vp = a.volumeProfile;

    // POC / VAH / VAL — singular fields
    const VP_SPECS = [
      { field: 'poc', label: 'POC', severity: 'good',
        hint: 'Entry at today\'s Point of Control — the most-accepted price by volume. Strong institutional reference.' },
      { field: 'vah', label: 'VAH', severity: 'info',
        hint: 'Entry at today\'s Value Area High — upper bound of the 70% volume zone. Often acts as resistance from inside, support from above.' },
      { field: 'val', label: 'VAL', severity: 'info',
        hint: 'Entry at today\'s Value Area Low — lower bound of the 70% volume zone. Often acts as support from inside, resistance from below.' },
    ];
    for (const spec of VP_SPECS) {
      const v = Number(vp[spec.field]);
      if (!Number.isFinite(v)) continue;
      const dist = Math.abs(entryPx - v);
      if (dist <= tol) {
        flags.push(flag(
          `vp-${spec.field}`,
          `Tagged ${spec.label}`,
          spec.severity,
          `${spec.hint} (level: ${v.toFixed(2)}, within ${tol.toFixed(2)} pts)`
        ));
      }
    }

    // HVN clusters — local volume maxima
    if (Array.isArray(vp.hvnClusters)) {
      vp.hvnClusters.forEach((price, idx) => {
        const v = Number(price);
        if (!Number.isFinite(v)) return;
        const dist = Math.abs(entryPx - v);
        if (dist <= tol) {
          flags.push(flag(
            `vp-hvn-${idx}`,
            `Tagged HVN #${idx + 1}`,
            'info',
            `Entry near a High-Volume Node (local volume peak at ${v.toFixed(2)}) — institutional reference cluster. Price tends to react here.`
          ));
        }
      });
    }

    // LVN clusters — local volume minima (thin areas)
    if (Array.isArray(vp.lvnClusters)) {
      vp.lvnClusters.forEach((price, idx) => {
        const v = Number(price);
        if (!Number.isFinite(v)) return;
        const dist = Math.abs(entryPx - v);
        if (dist <= tol) {
          flags.push(flag(
            `vp-lvn-${idx}`,
            `Tagged LVN #${idx + 1}`,
            'info',
            `Entry in a Low-Volume Node (local volume trough at ${v.toFixed(2)}) — price tends to traverse these zones quickly. Limited acceptance — momentum continuation more likely than reversal here.`
          ));
        }
      });
    }
  }

  return flags;
};

// Exports for tests
export { classifyStack };
